import { useCallback, useState } from 'react';

/**
 * How wide the scans page's two sized columns are, and where that is remembered.
 *
 * The controls panel is sized for the climb form it holds — a name, a ladder of
 * grades and a row of exception chips need room to breathe — not for the strip
 * of icon buttons above them.
 *
 * The photo is the column with no width of its own: it is the elastic one, and
 * takes whatever the other two leave. So the widths belong to the list of climbs
 * and to the controls panel, each of which has a natural size — a form is as
 * wide as its fields need.
 */
export const CLIMBS_WIDTH_STORAGE_KEY = 'gym_admin_scans_climbs_width';
export const DEFAULT_CLIMBS_WIDTH = 340;
export const MIN_CLIMBS_WIDTH = 260;
export const MAX_CLIMBS_WIDTH = 720;

export const PANEL_WIDTH_STORAGE_KEY = 'gym_admin_scans_panel_width';
export const DEFAULT_PANEL_WIDTH = 340;
export const MIN_PANEL_WIDTH = 260;
export const MAX_PANEL_WIDTH = 560;

export function clampWidth(width: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, width));
}

/**
 * The width stored under this key, or the fallback. A stored value outside the
 * range is pulled back into it rather than thrown away: the bounds can change,
 * and a column someone dragged wide should stay as wide as it is still allowed
 * to be.
 */
export function readStoredWidth(key: string, fallback: number, min: number, max: number): number {
  const stored = Number(localStorage.getItem(key));

  return Number.isFinite(stored) && stored > 0 ? clampWidth(stored, min, max) : fallback;
}

/** A column width that survives a reload, written as it is dragged. */
export function useStoredWidth(
  key: string,
  fallback: number,
  min: number,
  max: number
): [number, (width: number) => void] {
  const [width, setWidth] = useState(() => readStoredWidth(key, fallback, min, max));

  const change = useCallback(
    (next: number) => {
      const clamped = clampWidth(next, min, max);
      setWidth(clamped);
      localStorage.setItem(key, String(clamped));
    },
    [key, min, max]
  );

  return [width, change];
}
